import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  buildSessionEventsUrl,
  cancelSession,
  createSession,
  fetchSessionEvents,
  respondToSessionTool,
  sendSessionMessage,
} from '../../../services/api';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import {
  SESSION_ACTIVE_STATUSES,
  SESSION_SETTLED_STATUSES,
} from '../constants';
import { hasFEToolRenderer } from '../components/FEToolRenderer';
import {
  ApprovalSubmissionResult,
  ChatIntegration,
  ChatMessage,
  ChatMode,
  ModelProvider,
} from '../types';
import {
  requiresUiContextApproval,
  runUiContext,
  useUiContextTools,
} from '../uiContext';
import { AgentContext, AgentOrigin, SessionStatus } from '../../../types';
import { toApiContext } from '../utils';
import {
  INITIAL_TRANSLATION_STATE,
  SessionStreamEvent as SessionEvent,
  SessionTranslationState,
  appendUserMessage,
  translateSessionEvent,
} from './sessionEventTranslator';

const MAX_STREAM_RETRIES = 5;
const STREAM_RETRY_BASE_MS = 1000;

type Props = {
  chatHistory?: {
    threadId: string;
    messages: ChatMessage[];
  };
  context?: AgentContext;
  mode?: ChatMode;
  selectedModel?: ModelProvider;
  enabledIntegrations?: ChatIntegration[];
  enabled?: boolean;
};

export const shouldAutoRespondToToolOutput = (
  toolName?: string,
): toolName is string =>
  !!toolName &&
  !hasFEToolRenderer(toolName) &&
  !requiresUiContextApproval(toolName);

export const needsUserInput = (messages: ChatMessage[]): boolean => {
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type !== 'tool-invocation') continue;
      const invocation = part.toolInvocation;
      if (invocation.state !== 'call') continue;
      if (invocation.type === 'approval') return true;
      if (
        invocation.type === 'output' &&
        !shouldAutoRespondToToolOutput(invocation.toolName)
      ) {
        return true;
      }
    }
  }
  return false;
};

const findPendingFrontendOutput = (
  messages: ChatMessage[],
): {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
}[] => {
  const pending: {
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
  }[] = [];
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type !== 'tool-invocation') continue;
      const invocation = part.toolInvocation;
      const toolName = invocation.toolName;
      if (
        invocation.state === 'call' &&
        invocation.type === 'output' &&
        shouldAutoRespondToToolOutput(toolName)
      ) {
        pending.push({
          toolCallId: invocation.toolCallId,
          toolName,
          args: invocation.args ?? {},
        });
      }
    }
  }
  return pending;
};

const shouldClearLoadingOnWait = (
  nextMessages: ChatMessage[],
  pendingRequest: boolean,
  handledOutputIds: Set<string>,
): boolean => {
  if (needsUserInput(nextMessages)) return true;
  if (pendingRequest) return false;
  return findPendingFrontendOutput(nextMessages).every((item) =>
    handledOutputIds.has(item.toolCallId),
  );
};

const getApiErrorDetail = (err: unknown): string | undefined => {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return undefined;
  }
  const { response } = err;
  if (
    typeof response !== 'object' ||
    response === null ||
    !('data' in response)
  ) {
    return undefined;
  }
  const { data } = response;
  if (typeof data !== 'object' || data === null || !('detail' in data)) {
    return undefined;
  }
  return typeof data.detail === 'string' ? data.detail : undefined;
};

export const submitFrontendOutputsSequentially = async <T>(
  outputs: T[],
  canSubmit: () => boolean,
  submit: (output: T) => Promise<void>,
): Promise<void> => {
  for (const output of outputs) {
    if (!canSubmit()) break;
    await submit(output);
  }
};

const useSessionStream = ({
  chatHistory,
  context,
  mode = 'agent',
  selectedModel,
  enabledIntegrations,
  enabled = true,
}: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>(
    chatHistory?.messages || [],
  );
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Sticky for the hook's lifetime; replayed errors don't set it, since the
  // quota may have reset since.
  const [liveQuotaExhausted, setLiveQuotaExhausted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(
    chatHistory?.threadId,
  );
  const [status, setStatus] = useState<SessionStatus | undefined>(undefined);
  const [serverStatus, setServerStatus] = useState<SessionStatus | undefined>(
    undefined,
  );
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isFlushingFrontendOutputs, setIsFlushingFrontendOutputs] =
    useState(false);

  const sessionIdRef = useRef<string | undefined>(chatHistory?.threadId);
  const statusRef = useRef<SessionStatus | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const lastSeqRef = useRef<number>(-1);
  const pendingRequestRef = useRef(false);
  const messagesRef = useRef<ChatMessage[]>(chatHistory?.messages || []);
  const translationRef = useRef<SessionTranslationState>({
    ...INITIAL_TRANSLATION_STATE,
  });
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const handledOutputRef = useRef(new Set<string>());
  const failedOutputRef = useRef(new Set<string>());
  const isReplayingRef = useRef(false);
  const isFlushingFrontendOutputsRef = useRef(false);
  // Last status reported by the server. `status` is also set optimistically,
  // but tool responses are only accepted once the server is waiting_for_input.
  const serverStatusRef = useRef<SessionStatus | undefined>(undefined);
  const tools = useUiContextTools();
  const updateStatus = useCallback((nextStatus: SessionStatus | undefined) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);
  const updateServerStatus = useCallback(
    (nextStatus: SessionStatus | undefined) => {
      serverStatusRef.current = nextStatus;
      setServerStatus(nextStatus);
    },
    [],
  );
  const updateRequestPending = useCallback((isPending: boolean) => {
    pendingRequestRef.current = isPending;
    setIsRequestPending(isPending);
  }, []);
  const updateFlushingFrontendOutputs = useCallback((isFlushing: boolean) => {
    isFlushingFrontendOutputsRef.current = isFlushing;
    setIsFlushingFrontendOutputs(isFlushing);
  }, []);
  const canRespondNow = useCallback(
    () =>
      statusRef.current === 'waiting_for_input' &&
      serverStatusRef.current === 'waiting_for_input' &&
      !pendingRequestRef.current &&
      !isFlushingFrontendOutputsRef.current,
    [],
  );
  const canRespondToInput =
    status === 'waiting_for_input' &&
    serverStatus === 'waiting_for_input' &&
    !isRequestPending &&
    !isFlushingFrontendOutputs;

  const beginFrontendToolResponse = useCallback(
    (toolCallId: string, isAutomatic = false) => {
      const id = sessionIdRef.current;
      if (
        !id ||
        (!isAutomatic && !canRespondNow()) ||
        serverStatusRef.current !== 'waiting_for_input' ||
        pendingRequestRef.current ||
        handledOutputRef.current.has(toolCallId)
      ) {
        return undefined;
      }
      handledOutputRef.current.add(toolCallId);
      updateRequestPending(true);
      setIsLoading(true);
      updateStatus('queued');
      return id;
    },
    [canRespondNow, updateRequestPending, updateStatus],
  );

  const failFrontendToolResponse = useCallback(
    (toolCallId: string, err: unknown, isAutomatic = false) => {
      handledOutputRef.current.delete(toolCallId);
      if (isAutomatic) failedOutputRef.current.add(toolCallId);
      updateRequestPending(false);
      setError(getApiErrorDetail(err) || 'Failed to submit tool response');
      setIsLoading(false);
      updateStatus('waiting_for_input');
    },
    [updateRequestPending, updateStatus],
  );

  const applyFrontendTool = useCallback(
    async (
      toolCallId: string,
      toolName: string,
      args: Record<string, unknown>,
    ) => {
      if (!enabled) return;
      const isAutomatic = shouldAutoRespondToToolOutput(toolName);
      const id = beginFrontendToolResponse(toolCallId, isAutomatic);
      if (!id) return;
      try {
        const result = await runUiContext(toolName, args);
        await respondToSessionTool(id, {
          kind: 'output',
          tool_call_id: toolCallId,
          value: result.ok
            ? result.result
            : { error: result.error, nextSteps: result.nextSteps },
        });
        if (sessionIdRef.current !== id) return;
        updateRequestPending(false);
        if (needsUserInput(messagesRef.current)) {
          setIsLoading(false);
          updateStatus('waiting_for_input');
        }
      } catch (err: unknown) {
        if (sessionIdRef.current === id) {
          failFrontendToolResponse(toolCallId, err, isAutomatic);
        }
        throw err;
      }
    },
    [
      enabled,
      beginFrontendToolResponse,
      failFrontendToolResponse,
      updateRequestPending,
      updateStatus,
    ],
  );

  const flushPendingFrontendOutputs = useCallback(() => {
    if (
      !enabled ||
      isReplayingRef.current ||
      serverStatusRef.current !== 'waiting_for_input' ||
      pendingRequestRef.current ||
      isFlushingFrontendOutputsRef.current
    ) {
      return;
    }
    const pendingOutputs = findPendingFrontendOutput(
      messagesRef.current,
    ).filter(
      ({ toolCallId }) =>
        !handledOutputRef.current.has(toolCallId) &&
        !failedOutputRef.current.has(toolCallId),
    );
    if (pendingOutputs.length === 0) return;

    const id = sessionIdRef.current;
    updateFlushingFrontendOutputs(true);
    void (async () => {
      let hadFailure = false;
      try {
        await submitFrontendOutputsSequentially(
          pendingOutputs,
          () =>
            sessionIdRef.current === id &&
            serverStatusRef.current === 'waiting_for_input' &&
            !pendingRequestRef.current,
          async (pending) => {
            try {
              await applyFrontendTool(
                pending.toolCallId,
                pending.toolName,
                pending.args,
              );
            } catch {
              // Keep sending independent outputs after one submission fails.
              hadFailure = true;
            }
          },
        );
      } finally {
        if (sessionIdRef.current === id) {
          if (hadFailure) setIsLoading(false);
          updateFlushingFrontendOutputs(false);
          queueMicrotask(flushPendingFrontendOutputs);
        }
      }
    })();
  }, [applyFrontendTool, enabled, updateFlushingFrontendOutputs]);

  const rejectFrontendTool = useCallback(
    async (toolCallId: string) => {
      if (!enabled) return;
      const id = beginFrontendToolResponse(toolCallId);
      if (!id) return;
      try {
        await respondToSessionTool(id, {
          kind: 'output',
          tool_call_id: toolCallId,
          value: {
            applied: false,
            reason: 'The user rejected the proposed changes.',
          },
        });
        updateRequestPending(false);
        if (needsUserInput(messagesRef.current)) {
          setIsLoading(false);
          updateStatus('waiting_for_input');
        }
      } catch (err: unknown) {
        failFrontendToolResponse(toolCallId, err);
        throw err;
      }
    },
    [
      enabled,
      beginFrontendToolResponse,
      failFrontendToolResponse,
      updateRequestPending,
      updateStatus,
    ],
  );

  const commitMessages = useCallback((next: ChatMessage[]) => {
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const id = sessionIdRef.current;
    if (id) {
      void cancelSession(id).catch(() => undefined);
    }
    setIsLoading(false);
    updateStatus('cancelled');
  }, [updateStatus]);

  const handleEvent = useCallback(
    (event: SessionEvent) => {
      const seq = event.seq;
      const hasSeq = typeof seq === 'number' && seq >= 0;
      if (hasSeq) {
        if (seq <= lastSeqRef.current) return;
        lastSeqRef.current = seq;
      }
      setIsInitializing(false);

      const { messages: nextMessages, state: nextState } =
        translateSessionEvent(
          messagesRef.current,
          event,
          translationRef.current,
        );
      translationRef.current = nextState;
      if (nextMessages !== messagesRef.current) {
        commitMessages(nextMessages);
      }

      switch (event.type) {
        case 'chunk':
        case 'tool_call':
          setIsLoading(true);
          break;
        case 'tool_result':
          if (!needsUserInput(nextMessages)) {
            setIsLoading(true);
          }
          break;
        case 'status_change':
          if (event.status) {
            updateServerStatus(event.status);
            updateStatus(event.status);
          }
          if (event.status && SESSION_SETTLED_STATUSES.includes(event.status)) {
            setIsLoading(false);
            // The backend SSE stream blocks forever on a terminal session;
            // close it here so the connection doesn't dangle until the next
            // send (or a manual stop).
            abortRef.current?.abort();
            abortRef.current = null;
          } else if (event.status === 'waiting_for_input') {
            if (
              shouldClearLoadingOnWait(
                nextMessages,
                pendingRequestRef.current,
                handledOutputRef.current,
              )
            ) {
              setIsLoading(false);
            }
            flushPendingFrontendOutputs();
          } else if (event.status === 'running' || event.status === 'queued') {
            setIsLoading(true);
          }
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          break;
        case 'tool_awaiting_input':
          // Auto-responses are flushed once the server reports waiting_for_input;
          // the runner may still be tearing down and would 409 until then.
          if (needsUserInput(nextMessages)) {
            setIsLoading(false);
          }
          break;
        case 'error':
          setError(event.message || 'Agent error');
          setErrorCode(event.code ?? null);
          if (event.code === 'quota_exhausted' && !isReplayingRef.current) {
            setLiveQuotaExhausted(true);
          }
          setIsLoading(false);
          updateStatus('failed');
          abortRef.current?.abort();
          abortRef.current = null;
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          break;
        default:
          break;
      }
    },
    [
      queryClient,
      commitMessages,
      flushPendingFrontendOutputs,
      updateServerStatus,
      updateStatus,
    ],
  );

  const openStream = useCallback(
    (id: string, fromSeq: number, attempt: number = 0) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const scheduleReconnect = (nextAttempt: number) => {
        if (controller.signal.aborted || sessionIdRef.current !== id) return;
        if (nextAttempt >= MAX_STREAM_RETRIES) {
          if (abortRef.current === controller) abortRef.current = null;
          return;
        }
        const delay = STREAM_RETRY_BASE_MS * 2 ** nextAttempt;
        setTimeout(() => {
          if (sessionIdRef.current === id && !controller.signal.aborted) {
            openStream(id, lastSeqRef.current, nextAttempt);
          }
        }, delay);
      };

      const run = async () => {
        let gotData = false;
        try {
          const response = await fetch(buildSessionEventsUrl(id, fromSeq), {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
              Accept: 'application/x-ndjson',
            },
            signal: controller.signal,
          });
          if (!response.ok || !response.body) {
            const err = new Error(`Stream error: ${response.status}`);
            (err as any).fatal =
              response.status >= 400 && response.status < 500;
            throw err;
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            lines.forEach((line) => {
              if (controller.signal.aborted || sessionIdRef.current !== id) {
                return;
              }
              if (!line.trim()) return;
              try {
                const seqBefore = lastSeqRef.current;
                handleEvent(JSON.parse(line) as SessionEvent);
                if (lastSeqRef.current > seqBefore) gotData = true;
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Failed to parse session event', err);
              }
            });
          }
          scheduleReconnect(gotData ? 0 : attempt + 1);
        } catch (err: any) {
          if (controller.signal.aborted) return;
          setIsInitializing(false);
          if (err?.fatal) {
            setError('Lost connection to the conversation');
            setIsLoading(false);
            return;
          }
          scheduleReconnect(gotData ? 0 : attempt + 1);
        }
      };
      void run();
    },
    [handleEvent, user?.accessToken],
  );

  const sendMessage = useCallback(
    async (
      message: string,
      messageContext?: AgentContext,
      origin?: AgentOrigin,
    ) => {
      if (!enabled) return;
      const hadSession = Boolean(sessionIdRef.current);
      setError(null);
      setErrorCode(null);
      setIsLoading(true);
      updateRequestPending(true);
      commitMessages(
        appendUserMessage(
          messagesRef.current,
          message,
          new Date().toISOString(),
          messageContext ?? context,
        ),
      );
      translationRef.current.pendingUserEcho += 1;
      const apiContext = toApiContext(messageContext ?? context);
      const model = selectedModel?.modelName;
      try {
        if (!sessionIdRef.current) {
          const { session_id } = await createSession({
            prompt: message,
            mode,
            model,
            context: apiContext,
            environment_id: messageContext?.environmentIds?.[0],
            trigger_origin: origin,
            enabled_integrations: enabledIntegrations,
            tools,
          });
          sessionIdRef.current = session_id;
          setSessionId(session_id);
          updateStatus('queued');
          lastSeqRef.current = -1;
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
          openStream(session_id, -1);
        } else {
          const id = sessionIdRef.current;
          try {
            await sendSessionMessage(id, {
              prompt: message,
              mode,
              model,
              context: apiContext,
              enabled_integrations: enabledIntegrations,
              tools,
            });
          } catch (sendErr: any) {
            if (sendErr?.response?.status === 409) {
              await new Promise((resolve) => setTimeout(resolve, 800));
              await sendSessionMessage(id, {
                prompt: message,
                mode,
                model,
                context: apiContext,
                enabled_integrations: enabledIntegrations,
                tools,
              });
            } else {
              throw sendErr;
            }
          }
          updateStatus('queued');
          openStream(id, lastSeqRef.current);
          void queryClient.invalidateQueries({ queryKey: [AGENT_SESSIONS] });
        }
        updateRequestPending(false);
      } catch (err: any) {
        updateRequestPending(false);
        translationRef.current.pendingUserEcho = Math.max(
          0,
          translationRef.current.pendingUserEcho - 1,
        );
        const last = messagesRef.current[messagesRef.current.length - 1];
        if (last?.role === 'user') {
          commitMessages(messagesRef.current.slice(0, -1));
        }
        setError(err?.response?.data?.detail || 'Failed to send message');
        setIsLoading(false);
        // Existing session: clear status so canSend re-derives instead of
        // optimistically unblocking. New chat: safely idle.
        updateStatus(hadSession ? undefined : 'idle');
      }
    },
    [
      context,
      enabled,
      mode,
      selectedModel,
      enabledIntegrations,
      tools,
      openStream,
      queryClient,
      commitMessages,
      updateRequestPending,
      updateStatus,
    ],
  );

  const approveToolUse = useCallback(
    async (
      approved: boolean,
      interruptId?: string,
      newArgs?: Record<string, unknown>,
    ): Promise<ApprovalSubmissionResult> => {
      const id = sessionIdRef.current;
      if (!id || !interruptId || !canRespondNow()) return 'not_ready';
      setError(null);
      setErrorCode(null);
      setIsLoading(true);
      updateRequestPending(true);
      updateStatus('queued');
      openStream(id, lastSeqRef.current);
      try {
        await respondToSessionTool(id, {
          kind: 'approval',
          tool_call_id: interruptId,
          approved,
          args_override:
            newArgs && Object.keys(newArgs).length > 0 ? newArgs : undefined,
        });
        if (sessionIdRef.current !== id) return 'not_ready';
        updateRequestPending(false);
        const seqBeforeRefresh = lastSeqRef.current;
        try {
          const { status: confirmedStatus } = await fetchSessionEvents(
            id,
            seqBeforeRefresh,
          );
          if (
            sessionIdRef.current === id &&
            !pendingRequestRef.current &&
            lastSeqRef.current === seqBeforeRefresh &&
            confirmedStatus
          ) {
            updateServerStatus(confirmedStatus);
            updateStatus(confirmedStatus);
            if (confirmedStatus === 'waiting_for_input') {
              setIsLoading(false);
              flushPendingFrontendOutputs();
            }
          }
        } catch {
          // The stream or poll can still report the next status.
        }
        return 'delivered';
      } catch (err: any) {
        if (sessionIdRef.current === id) {
          updateRequestPending(false);
          setError(
            err?.response?.data?.detail || 'Failed to submit tool response',
          );
          setIsLoading(false);
          updateStatus('waiting_for_input');
        }
        return 'failed';
      }
    },
    [
      canRespondNow,
      openStream,
      updateRequestPending,
      updateStatus,
      updateServerStatus,
      flushPendingFrontendOutputs,
    ],
  );

  const userInteraction = useCallback(
    (val: Record<string, any>) => {
      if (canRespondNow() && typeof val.selectedAction === 'string') {
        void sendMessage(val.selectedAction);
      }
    },
    [canRespondNow, sendMessage],
  );

  const loadSession = useCallback(
    async (id: string) => {
      abortRef.current?.abort();
      sessionIdRef.current = id;
      setSessionId(id);
      handledOutputRef.current.clear();
      failedOutputRef.current.clear();
      updateServerStatus(undefined);
      updateRequestPending(false);
      updateFlushingFrontendOutputs(false);
      updateStatus(undefined);
      lastSeqRef.current = -1;
      translationRef.current = { ...INITIAL_TRANSLATION_STATE };
      commitMessages([]);
      setError(null);
      setErrorCode(null);
      setIsLoading(false);
      setIsInitializing(true);
      try {
        const { status: loadedStatus, events } = await fetchSessionEvents(
          id,
          -1,
        );
        if (sessionIdRef.current !== id) return;
        // Top-level status may be null; fall back to the latest event status
        // so we don't clobber an active session with idle.
        const latestEventStatus = [...events]
          .reverse()
          .map((event) => (event as SessionEvent).status)
          .find((eventStatus): eventStatus is SessionStatus => !!eventStatus);
        const nextStatus = loadedStatus ?? latestEventStatus ?? 'idle';
        isReplayingRef.current = true;
        try {
          events.forEach((event) => handleEvent(event as SessionEvent));
        } finally {
          isReplayingRef.current = false;
        }
        updateServerStatus(nextStatus);
        updateStatus(nextStatus);
        setIsInitializing(false);
        flushPendingFrontendOutputs();
        if (SESSION_ACTIVE_STATUSES.includes(nextStatus)) {
          openStream(id, lastSeqRef.current);
        }
      } catch (err: any) {
        if (sessionIdRef.current !== id) return;
        setIsInitializing(false);
        updateStatus('idle');
        setError(err?.response?.data?.detail || 'Failed to load conversation');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleEvent,
      openStream,
      applyFrontendTool,
      flushPendingFrontendOutputs,
      updateServerStatus,
      updateRequestPending,
      updateFlushingFrontendOutputs,
      updateStatus,
    ],
  );

  const continueChat = useCallback(() => {
    const id = sessionIdRef.current;
    if (!id) return;
    failedOutputRef.current.clear();
    setError(null);
    setErrorCode(null);
    openStream(id, lastSeqRef.current);
    flushPendingFrontendOutputs();
  }, [openStream, flushPendingFrontendOutputs]);

  // Poll on confirmed session lifecycle states (not request-local isLoading):
  // idle SSE connections often die while waiting for tool approval, and
  // approveToolUse alone isn't enough if reconnects were exhausted.
  const shouldPollEvents =
    status === 'waiting_for_input' ||
    status === 'running' ||
    status === 'queued';

  useEffect(() => {
    if (!enabled || !shouldPollEvents || !sessionId) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Polling is a fallback for when the live SSE stream isn't connected.
      // While a stream is open (abortRef set) it already delivers events, so
      // skip the redundant /events/history request. Also skip while a send /
      // tool-response request is in flight so a stale history response cannot
      // clear optimistic loading/queued state.
      if (abortRef.current || pendingRequestRef.current) return;
      try {
        const { status: polledStatus, events } = await fetchSessionEvents(
          sessionId,
          lastSeqRef.current,
        );
        if (cancelled || pendingRequestRef.current) return;
        events.forEach((event) => handleEvent(event as SessionEvent));
        if (polledStatus) {
          updateServerStatus(polledStatus);
          updateStatus(polledStatus);
        }
        if (polledStatus && SESSION_SETTLED_STATUSES.includes(polledStatus)) {
          setIsLoading(false);
        } else if (
          polledStatus === 'waiting_for_input' &&
          shouldClearLoadingOnWait(
            messagesRef.current,
            pendingRequestRef.current,
            handledOutputRef.current,
          )
        ) {
          setIsLoading(false);
        }
        flushPendingFrontendOutputs();
      } catch {
        // noop
      }
    };
    const interval = setInterval(tick, 2500);
    const initial = setTimeout(tick, 1200);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(initial);
    };
  }, [
    enabled,
    shouldPollEvents,
    sessionId,
    handleEvent,
    flushPendingFrontendOutputs,
    updateServerStatus,
    updateStatus,
  ]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (enabled && chatHistory?.threadId) {
      void loadSession(chatHistory.threadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory?.threadId]);

  useEffect(() => {
    flushPendingFrontendOutputs();
  }, [tools, messages, flushPendingFrontendOutputs]);

  useEffect(() => {
    if (enabled) return;
    const historyMessages = chatHistory?.messages ?? [];
    messagesRef.current = historyMessages;
    setMessages(historyMessages);
    sessionIdRef.current = chatHistory?.threadId;
    setSessionId(chatHistory?.threadId);
    lastSeqRef.current = -1;
    handledOutputRef.current = new Set<string>();
    failedOutputRef.current = new Set<string>();
    updateServerStatus(undefined);
    updateRequestPending(false);
    updateFlushingFrontendOutputs(false);
    updateStatus(undefined);
    translationRef.current = { ...INITIAL_TRANSLATION_STATE };
  }, [
    enabled,
    chatHistory?.threadId,
    chatHistory?.messages,
    updateServerStatus,
    updateRequestPending,
    updateFlushingFrontendOutputs,
    updateStatus,
  ]);

  const consolidatedMessages = useMemo(
    () =>
      messages
        .filter((message) => !message.hidden)
        .reduce<ChatMessage[]>((result, message) => {
          const lastMessage = result[result.length - 1];
          if (
            message.role === 'assistant' &&
            lastMessage?.role === 'assistant'
          ) {
            lastMessage.parts.push(...message.parts);
            return result;
          }
          result.push({ ...message, parts: [...message.parts] });
          return result;
        }, []),
    [messages],
  );

  // Mirror the backend send_message guard: accept only idle/cancelled/
  // waiting_for_input (else the POST 409s). A new chat with no session can send.
  const canSend = useMemo(() => {
    if (!sessionId) return true;
    if (isInitializing) return false;
    return (
      status === 'idle' ||
      status === 'cancelled' ||
      status === 'waiting_for_input'
    );
  }, [sessionId, isInitializing, status]);

  return {
    threadId: sessionId || '',
    canSend,
    sendMessage,
    messages,
    consolidatedMessages,
    stop,
    error,
    errorCode,
    liveQuotaExhausted,
    isLoading,
    isInitializing,
    canRespondToInput,
    continueChat,
    approveToolUse,
    applyFrontendTool,
    rejectFrontendTool,
    userInteraction,
  };
};

export default useSessionStream;
